import React, { useMemo } from "react";
import Dialog from "@mui/material/Dialog";
import CloseIcon from "@mui/icons-material/Close";
import { trainingFormStatusLabels } from "../constant/constants";
import {
  formatZAR,
  getFormHistoryTrainingColumns,
} from "../constant/utils/commonUtils";
import {
  formatDateForDisplay,
  formatDateTimeForDisplay,
} from "../constant/utils/datetimeUtils";
import CButton from "./CButton";

const displayValue = (value) => {
  if (value == null) return "";
  if (typeof value === "string" && value.trim() === "") return "";
  return value;
};

const formatStatus = (status) =>
  displayValue(trainingFormStatusLabels[status] || status);

const sectionTitleClass =
  "bg-white text-lg font-semibold text-gray-700 2xl:text-xl 2xl:leading-xl lg:text-base lg:leading-base text-sm leading-sm absolute md:top-[-16px] top-[-12px] left-[16px] px-1";

const sectionBoxClass =
  "border border-blue-200 bg-white rounded-lg lg:p-6 p-4 relative md:mt-8 mt-6";

const labelClass =
  "2xl:text-base text-sm 2xl:leading-base leading-sm text-blue-300 font-medium";

const valueClass = "text-gray-700";

const buildSkillTrainingGroups = (snapshot) => {
  const skills = Array.isArray(snapshot?.skill_requirements)
    ? snapshot.skill_requirements
    : [];
  const trainings = Array.isArray(snapshot?.selected_trainings)
    ? snapshot.selected_trainings
    : [];

  return skills.map((skill, index) => {
    const skillId = skill.id;
    const linked = trainings.filter(
      (st) => st.skill_requirements_id === skillId,
    );
    return {
      key: skillId || `skill-${index}`,
      skill,
      trainings: linked,
    };
  });
};

const allocationBySelectedTrainingId = (snapshot) => {
  const map = {};
  const rows = snapshot?.allocate_training_program;
  if (Array.isArray(rows)) {
    rows.forEach((atp) => {
      if (atp?.selected_training_id) {
        map[atp.selected_training_id] = atp;
      }
    });
  }
  return map;
};

const formatCourseType = (skill) =>
  displayValue(
    skill?.training_type || skill?.program_duration?.replace(/_/g, " "),
  );

const mapTrainingRows = (snapshot) => {
  const trainings = Array.isArray(snapshot?.selected_trainings)
    ? snapshot.selected_trainings
    : [];
  const allocMap = allocationBySelectedTrainingId(snapshot);
  return trainings.map((st) => {
    const atp = allocMap[st.id];
    const t = st.training || {};
    const additionalFields =
      st.allocated_additional_field ||
      atp?.additional_field ||
      atp?.additionalField ||
      {};
    return {
      id: st.id || st.training_id,
      name: displayValue(t.name || t.training_name),
      program_duration: displayValue(t.program_duration),
      allocated_training_category: displayValue(
        st.allocated_training_category || atp?.training_category,
      ),
      allocated_training_type: displayValue(
        st.allocated_training_type || atp?.training_type,
      ),
      cost: t.cost ?? t.training_expense,
      additionalFields,
    };
  });
};

const formatTrainingCell = (value, col) => {
  if (value == null || value === "") return "";
  if (col.isCost) {
    const num = Number(value);
    return !Number.isNaN(num) ? formatZAR(num) : displayValue(value);
  }
  if (col.additionalField) {
    const ft = String(col.additionalField.type || "").toLowerCase();
    const fallback = displayValue(value);
    if (ft === "datetime") {
      return formatDateTimeForDisplay(value, fallback);
    }
    if (ft === "date" || ft === "datepicker") {
      return formatDateForDisplay(value, fallback);
    }
  }
  return displayValue(value);
};

const getTrainingCellValue = (row, col) => {
  if (col.getValue) return col.getValue(row);
  return row[col.key];
};

const employeeDetailsFromSnapshot = (snapshot) => {
  const cu = snapshot?.company_user;
  if (!cu) {
    return {
      empId: "",
      name: "",
      designation: "",
      department: "",
      manager: "",
    };
  }
  const name = displayValue(
    [cu.first_name, cu.last_name].filter(Boolean).join(" ") || cu.data?.Name,
  );
  return {
    empId: displayValue(cu.emp_id),
    name,
    designation: displayValue(cu.designation || cu.data?.Designation),
    department: displayValue(cu.department || cu.data?.Department),
    manager: displayValue(cu.department_head_name),
  };
};

const SectionBox = ({ title, children, className = "" }) => (
  <div className={`${sectionBoxClass} ${className}`}>
    <p className={sectionTitleClass}>{title}</p>
    {children}
  </div>
);

const DetailItem = ({ label, value, changed }) => (
  <p className={`${labelClass} ${changed ? "rounded px-1 -mx-1 bg-amber-50" : ""}`}>
    {label}: <span className={valueClass}>{displayValue(value)}</span>
  </p>
);

const SkillsTable = ({ groups, compareGroups }) => {
  const compareBySkillId = useMemo(() => {
    const map = {};
    (compareGroups || []).forEach((g) => {
      if (g.skill?.id) map[g.skill.id] = g;
    });
    return map;
  }, [compareGroups]);

  const isChanged = (a, b) => String(a ?? "") !== String(b ?? "");

  if (!groups.length) {
    return (
      <p className="text-sm text-gray-500 italic mt-2">No skills recorded</p>
    );
  }

  return (
    <div className="border border-blue-200 bg-white rounded-lg relative overflow-x-auto md:my-2 my-2 mt-4">
      <table className="md:w-full w-[800px] employee_form_table">
        <thead>
          <tr>
            <th className="text-left bg-blue-100 2xl:p-3 p-2 font-semibold text-sm leading-sm">
              Skills Required
            </th>
            <th className="text-left bg-blue-100 2xl:p-3 p-2 font-semibold text-sm leading-sm">
              Course Type
            </th>
            <th className="text-left bg-blue-100 2xl:p-3 p-2 font-semibold text-sm leading-sm">
              Priority Level
            </th>
            <th className="text-left bg-blue-100 2xl:p-3 p-2 font-semibold text-sm leading-sm">
              Skills Requirement Motivation
            </th>
          </tr>
        </thead>
        <tbody>
          {groups.map((group) => {
            const skill = group.skill;
            const cs = compareBySkillId[skill?.id]?.skill;
            const courseType = formatCourseType(skill);
            const compareCourseType = formatCourseType(cs);
            const cellClass = (field) =>
              isChanged(skill?.[field], cs?.[field])
                ? "bg-amber-50"
                : "";
            return (
              <tr key={group.key}>
                <td
                  className={`text-left 2xl:p-3 p-2 text-sm leading-sm text-gray-700 ${cellClass("skills_required")}`}
                >
                  {displayValue(skill.skills_required)}
                </td>
                <td
                  className={`text-left 2xl:p-3 p-2 text-sm leading-sm text-gray-700 ${
                    courseType !== compareCourseType ? "bg-amber-50" : ""
                  }`}
                >
                  {courseType}
                </td>
                <td
                  className={`text-left 2xl:p-3 p-2 text-sm leading-sm text-gray-700 ${cellClass("priority_level")}`}
                >
                  {displayValue(skill.priority_level)}
                </td>
                <td
                  className={`text-left 2xl:p-3 p-2 text-sm leading-sm text-gray-700 ${cellClass("skills_requirement_motivation")}`}
                >
                  {displayValue(skill.skills_requirement_motivation)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const TrainingsTable = ({ rows, compareRows, setaTemplate }) => {
  const compareById = useMemo(() => {
    const map = {};
    (compareRows || []).forEach((r) => {
      if (r.id) map[r.id] = r;
    });
    return map;
  }, [compareRows]);

  const columns = useMemo(
    () => getFormHistoryTrainingColumns(setaTemplate, rows, compareRows),
    [setaTemplate, rows, compareRows],
  );

  const isChanged = (a, b) => String(a ?? "") !== String(b ?? "");

  if (!rows.length) {
    return (
      <p className="text-sm text-gray-500 italic mt-4">No trainings selected</p>
    );
  }

  return (
    <div className="border border-blue-200 bg-white rounded-lg relative w-full overflow-x-auto md:my-4 my-3 employee_form_traning_table mt-4">
      <table className="md:w-full w-full">
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                className="text-left bg-blue-100 2xl:p-3 p-2 font-semibold text-sm leading-sm whitespace-nowrap"
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const other = compareById[row.id];
            return (
              <tr key={row.id}>
                {columns.map((col) => {
                  const raw = getTrainingCellValue(row, col);
                  const otherRaw = other ? getTrainingCellValue(other, col) : "";
                  return (
                    <td
                      key={col.key}
                      className={`text-left 2xl:p-3 p-2 text-sm text-gray-700 whitespace-nowrap ${
                        isChanged(raw, otherRaw) ? "bg-amber-50" : ""
                      }`}
                    >
                      {formatTrainingCell(raw, col)}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const SnapshotColumn = ({ title, snapshot, compareSnapshot, setaTemplate }) => {
  const form = snapshot?.training_survey_form || {};
  const status = snapshot?.latest_form_status?.status;
  const compareForm = compareSnapshot?.training_survey_form || {};
  const compareStatus = compareSnapshot?.latest_form_status?.status;
  const employee = employeeDetailsFromSnapshot(snapshot);
  const groups = useMemo(() => buildSkillTrainingGroups(snapshot), [snapshot]);
  const compareGroups = useMemo(
    () => buildSkillTrainingGroups(compareSnapshot),
    [compareSnapshot],
  );
  const trainingRows = useMemo(() => mapTrainingRows(snapshot), [snapshot]);
  const compareTrainingRows = useMemo(
    () => mapTrainingRows(compareSnapshot),
    [compareSnapshot],
  );

  const isChanged = (a, b) => String(a ?? "") !== String(b ?? "");

  return (
    <div className="flex flex-col min-h-0 h-full overflow-y-auto pr-1">
      <p className="text-blue-500 font-semibold text-base lg:text-lg mb-4 sticky top-0 bg-white z-10 py-1 border-b border-blue-100">
        {title}
      </p>

      <SectionBox title="Employee Details" className="!mt-4">
        <div className="grid lg:grid-cols-2 grid-cols-1 sm:gap-2 gap-1 mt-2">
          <DetailItem label="Employee ID" value={employee.empId} />
          <DetailItem label="Employee Name" value={employee.name} />
          <DetailItem label="Designation" value={employee.designation} />
          <DetailItem label="Department" value={employee.department} />
          <DetailItem label="Manager" value={employee.manager} />
        </div>
      </SectionBox>

      <SectionBox title="Form status & goals">
        <div className="grid grid-cols-1 gap-1 mt-2">
          <DetailItem
            label="Workflow status"
            value={formatStatus(status)}
            changed={isChanged(status, compareStatus)}
          />
          <DetailItem
            label="Short Term Goal"
            value={form.short_term_goal}
            changed={isChanged(form.short_term_goal, compareForm.short_term_goal)}
          />
          <DetailItem
            label="Long Term Goal"
            value={form.long_term_goal}
            changed={isChanged(form.long_term_goal, compareForm.long_term_goal)}
          />
          <DetailItem
            label="Any Notes"
            value={form.notes}
            changed={isChanged(form.notes, compareForm.notes)}
          />
        </div>
      </SectionBox>

      <SectionBox title="Training Needs Details">
        <SkillsTable groups={groups} compareGroups={compareGroups} />
        <p className="text-sm font-medium text-gray-700 mt-4 mb-1">
          Selected Trainings
        </p>
        <TrainingsTable
          rows={trainingRows}
          compareRows={compareTrainingRows}
          setaTemplate={setaTemplate}
        />
      </SectionBox>
    </div>
  );
};

const FormHistoryCompareDialog = ({ open, onClose, historyRow, setaTemplate }) => {
  const oldSnapshot = historyRow?.old_value;
  const newSnapshot = historyRow?.new_value;

  const meta = useMemo(() => {
    if (!historyRow) return null;
    return {
      action: displayValue(
        trainingFormStatusLabels[historyRow.action] || historyRow.action,
      ),
      comment: historyRow.comment || "",
      name: displayValue(historyRow.name),
      role: displayValue(historyRow.role),
      dateTime:
        historyRow.date && historyRow.time
          ? `${historyRow.date} ${historyRow.time}`
          : "",
    };
  }, [historyRow]);

  return (
    <Dialog fullScreen open={open} onClose={onClose} className="CDialog">
      <div className="h-full flex flex-col bg-white overflow-hidden">
        <div className="flex items-start justify-between gap-4 px-4 lg:px-6 py-4 border-b border-blue-200 shrink-0">
          <div className="min-w-0">
            <h2 className="!font-[Poppins] xl:text-2xl xl:leading-2xl text-xl leading-xl text-blue-500 font-semibold">
              Form history
            </h2>
            {meta && (
              <p className="text-sm text-blue-300 font-medium mt-1 truncate">
                {meta.action} · {meta.name} ({meta.role}) · {meta.dateTime}
              </p>
            )}
          </div>
          <CButton
            type="button"
            onClick={onClose}
            className="!p-0 shrink-0 text-blue-500 hover:text-blue-700"
            aria-label="Close"
          >
            <CloseIcon />
          </CButton>
        </div>

        {meta?.comment ? (
          <div className="px-4 lg:px-6 py-3 bg-blue-50 border-b border-blue-200 shrink-0">
            <p className={`${labelClass} !text-xs uppercase tracking-wide`}>
              Comment
            </p>
            <p className={`${valueClass} text-sm mt-1 whitespace-pre-wrap`}>
              {meta.comment}
            </p>
          </div>
        ) : null}

        <div className="flex-1 min-h-0 overflow-hidden px-4 lg:px-6 py-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 h-full min-h-0">
            <SnapshotColumn
              title="Old form"
              snapshot={oldSnapshot}
              compareSnapshot={newSnapshot}
              setaTemplate={setaTemplate}
            />
            <SnapshotColumn
              title="New form"
              snapshot={newSnapshot}
              compareSnapshot={oldSnapshot}
              setaTemplate={setaTemplate}
            />
          </div>
        </div>

        <p className="text-xs text-gray-500 text-center py-2 border-t border-blue-100 shrink-0">
          Highlighted cells differ between old and new form.
        </p>
      </div>
    </Dialog>
  );
};

export default FormHistoryCompareDialog;
